<template>
  <section class="metadata-section">
    <div class="metadata-header">
      <div class="header-left">
        <span class="label">Track History</span>
        <button class="btn-scroll-active" @click="scrollToActive" title="Jump to active track" v-if="!searchMode">
          <AppIcon name="crosshairs" />
        </button>
      </div>
      <div class="header-right">
        <div class="search-box" :class="{ active: searchFocused || searchQuery }">
          <AppIcon name="search" class="search-icon" />
          <input 
            type="text" 
            v-model="searchQuery" 
            @focus="searchFocused = true"
            @blur="searchFocused = false"
            :placeholder="searchMode ? 'Search...' : `Search in ${stationName}...`"
            ref="searchInput"
          >
          <button v-if="searchQuery || searchMode" class="btn-clear-search" @click="clearSearch" title="Clear search">
            <AppIcon name="times" />
          </button>
        </div>
        <span v-if="!searchMode" class="current-date">{{ currentVisibleDate }}</span>
      </div>
    </div>
    
    <!-- Search Results Mode -->
    <div v-if="searchMode" class="metadata-list search-results" ref="searchResultsList" @scroll="onSearchScroll">
      <div v-if="searchLoading" class="search-status">
        <AppIcon name="spinner" spin />Searching...
      </div>
      <div v-else-if="searchResults.length === 0" class="search-status">
        No results found for "{{ searchExecutedQuery }}"
      </div>
      <template v-else>
        <div class="search-results-header">
          {{ searchResults.length }}{{ searchHasMore ? '+' : '' }} result{{ searchResults.length !== 1 ? 's' : '' }} for "{{ searchExecutedQuery }}"
        </div>
        <div v-for="m in searchResults" :key="m.id" 
             class="track-item search-result"
             @click="goToSearchResult(m)">
          <div class="track-info">
            <div class="track-title">{{ formatTitle(m) }}<span v-if="station?.sourceMode === 'fingerprint' && m.artist" class="track-artist"> - {{ m.artist }}</span></div>
            <div class="track-type">{{ getTypeLabel(m.type) }}</div>
          </div>
          <div class="track-datetime">
            <div class="track-date">{{ formatDate(m.timestamp) }}</div>
            <div class="track-time">{{ fmtTime(m.timestamp) }}</div>
          </div>
        </div>
        <div v-if="searchHasMore" class="load-more" ref="loadMoreSearch">
          <span v-if="searchLoadingMore">Loading more...</span>
          <span v-else>Loading more...</span>
        </div>
      </template>
    </div>
    
    <!-- Normal Track List Mode -->
    <div v-else class="metadata-list" id="track-list" ref="trackList" @scroll="onScroll">
      <div v-if="metadataHasMoreNewer" class="load-more" ref="loadMoreNewer">Loading newer...</div>
      <template v-if="metadataList.length > 0">
        <div v-for="m in metadataList" :key="m.id" 
             :class="['track-item', { active: m.id === activeMetadataId }]" 
             :data-id="m.id"
             :data-timestamp="m.timestamp"
             @click="goToTrack(m)">
          <div class="track-info">
            <div class="track-title">{{ formatTitle(m) }}<span v-if="station?.sourceMode === 'fingerprint' && m.artist" class="track-artist"> - {{ m.artist }}</span></div>
            <div class="track-type">{{ getTypeLabel(m.type) }}</div>
          </div>
          <div class="track-time">{{ fmtTime(m.timestamp) }}</div>
        </div>
      </template>
      <div v-else class="empty-message">No tracks yet</div>
      <div v-if="metadataHasMore" class="load-more" ref="loadMoreOlder">Loading more...</div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getTypeCategory, fmtTime, formatBytes } from '../services/helpers'
import { authFetch } from '../services/api'

export default {
  name: 'TrackList',
  data() {
    return {
      olderObserver: null,
      newerObserver: null,
      searchObserver: null,
      userClickedTrack: false,
      observersPaused: false,
      visibleDate: null,
      // Search state
      searchQuery: '',
      searchExecutedQuery: '',
      searchResults: [],
      searchLoading: false,
      searchLoadingMore: false,
      searchHasMore: false,
      searchMode: false,
      searchFocused: false,
      searchDebounceTimer: null
    }
  },
  computed: {
    ...mapState(['segments', 'metadataList', 'metadataHasMore', 'metadataHasMoreNewer', 'metadataLoading', 'activeMetadataId', 'isLive', 'station']),
    ...mapGetters(['typeCategories', 'defaultTypeColor', 'stationName']),
    stationSlug() {
      return this.station?.slug || ''
    },
    segmentCount() { return this.segments.length },
    storageStr() {
      let total = 0
      for (const seg of this.segments) total += seg.bytes || 0
      return total > 0 ? formatBytes(total) : '--'
    },
    currentVisibleDate() {
      if (this.visibleDate) return this.visibleDate
      // Default to first item's date if no scroll yet
      if (this.metadataList.length > 0) {
        return this.formatDate(this.metadataList[0].timestamp)
      }
      return '--'
    }
  },
  watch: {
    // Emit search mode changes to parent
    searchMode(val) {
      this.$emit('search-mode-change', val)
      if (!val && this.searchObserver) {
        this.searchObserver.disconnect()
        this.searchObserver = null
      }
    },
    // Set up search observer when results change
    searchResults() {
      this.$nextTick(() => {
        this.setupSearchObserver()
      })
    },
    searchHasMore() {
      this.$nextTick(() => {
        this.setupSearchObserver()
      })
    },
    // Debounced search as you type
    searchQuery(val) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      
      if (!val.trim()) {
        this.clearSearch()
        return
      }
      
      this.searchDebounceTimer = setTimeout(() => {
        this.doSearch()
      }, 250)
    },
    activeMetadataId() {
      // Skip scroll if user just clicked a track - they already see it
      if (this.userClickedTrack) {
        this.userClickedTrack = false
        return
      }
      
      // Only auto-scroll if:
      // 1. We're live AND already near the top (user hasn't scrolled down)
      // 2. OR it's a navigation action (not live)
      const list = this.$refs.trackList
      const isNearTop = !list || list.scrollTop < 50
      
      if (this.isLive && !isNearTop) {
        // User has scrolled down while live - don't interrupt them
        return
      }
      
      // Use setTimeout like original to ensure DOM is rendered
      setTimeout(() => this.scrollToActive(), 50)
    },
    metadataList() {
      // Pause observers briefly to prevent immediate re-trigger after list update
      this.observersPaused = true
      this.$nextTick(() => {
        this.setupObservers()
        this.updateVisibleDate()
        setTimeout(() => { this.observersPaused = false }, 300)
      })
    },
    metadataHasMoreNewer() {
      this.observersPaused = true
      this.$nextTick(() => {
        this.setupObservers()
        setTimeout(() => { this.observersPaused = false }, 300)
      })
    },
    metadataHasMore() {
      this.observersPaused = true
      this.$nextTick(() => {
        this.setupObservers()
        setTimeout(() => { this.observersPaused = false }, 300)
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupObservers()
      this.updateVisibleDate()
      setTimeout(() => this.scrollToActive(), 100)
    })
  },
  beforeUnmount() {
    if (this.olderObserver) this.olderObserver.disconnect()
    if (this.newerObserver) this.newerObserver.disconnect()
    if (this.searchObserver) this.searchObserver.disconnect()
  },
  methods: {
    fmtTime,
    formatTitle(m) { return m.raw || m.title || 'Unknown' },
    getTypeLabel(type) { 
      return getTypeCategory(type, this.typeCategories, this.defaultTypeColor).label 
    },
    
    formatDate(ts) {
      const d = new Date(ts)
      const options = { month: 'short', day: 'numeric', year: 'numeric' }
      return d.toLocaleDateString('en-US', options)
    },
    
    onScroll() {
      this.updateVisibleDate()
    },
    
    onSearchScroll() {
      // Observer handles loading more
    },
    
    setupSearchObserver() {
      if (this.searchObserver) {
        this.searchObserver.disconnect()
      }
      
      const list = this.$refs.searchResultsList
      const loadMoreEl = this.$refs.loadMoreSearch
      
      if (!list || !loadMoreEl || !this.searchHasMore) return
      
      this.searchObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.searchLoadingMore && this.searchHasMore) {
            this.loadMoreSearchResults()
          }
        }
      }, { root: list, rootMargin: '100px', threshold: 0 })
      
      this.searchObserver.observe(loadMoreEl)
    },
    
    updateVisibleDate() {
      const list = this.$refs.trackList
      if (!list) return
      
      // Find the first visible track item
      const items = list.querySelectorAll('.track-item')
      const listRect = list.getBoundingClientRect()
      
      for (const item of items) {
        const itemRect = item.getBoundingClientRect()
        // Check if item is at or below the top of the list container
        if (itemRect.top >= listRect.top - 10) {
          const timestamp = parseInt(item.dataset.timestamp, 10)
          if (timestamp) {
            this.visibleDate = this.formatDate(timestamp)
          }
          break
        }
      }
    },
    
    goToTrack(m) {
      // Flag to skip scroll - user clicked this item so they already see it
      this.userClickedTrack = true
      // Track is already in the list, so use navigateToTrack (no metadata refetch)
      this.$store.commit('SET_ACTIVE_METADATA_ID', m.id)
      this.$store.dispatch('navigateToTrack', m.timestamp)
    },
    
    // Search methods
    async doSearch() {
      const query = this.searchQuery.trim()
      if (!query) {
        this.clearSearch()
        return
      }
      
      this.searchMode = true
      this.searchLoading = true
      this.searchExecutedQuery = query
      this.searchResults = []
      this.searchHasMore = false
      
      try {
        const response = await authFetch(`/api/s/${this.stationSlug}/metadata/search?q=${encodeURIComponent(query)}&limit=50`)
        if (response.ok) {
          const data = await response.json()
          this.searchResults = data.results || []
          this.searchHasMore = data.hasMore || false
        }
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        this.searchLoading = false
      }
    },
    
    async loadMoreSearchResults() {
      if (this.searchLoadingMore || !this.searchHasMore || this.searchResults.length === 0) return
      
      this.searchLoadingMore = true
      const lastResult = this.searchResults[this.searchResults.length - 1]
      
      try {
        const response = await authFetch(
          `/api/s/${this.stationSlug}/metadata/search?q=${encodeURIComponent(this.searchExecutedQuery)}&limit=50&before=${lastResult.timestamp}`
        )
        if (response.ok) {
          const data = await response.json()
          const newResults = data.results || []
          this.searchResults = [...this.searchResults, ...newResults]
          this.searchHasMore = data.hasMore || false
        }
      } catch (err) {
        console.error('Load more search error:', err)
      } finally {
        this.searchLoadingMore = false
      }
    },
    
    clearSearch() {
      this.searchQuery = ''
      this.searchExecutedQuery = ''
      this.searchResults = []
      this.searchMode = false
      this.searchLoading = false
      this.searchLoadingMore = false
      this.searchHasMore = false
    },
    
    goToSearchResult(m) {
      // Clear search and jump to that timestamp
      this.clearSearch()
      // Use gotoTimestamp which will reload metadata around that point
      this.$store.dispatch('gotoTimestamp', m.timestamp)
    },
    
    scrollToActive() {
      const list = this.$refs.trackList
      if (!list) return
      const active = list.querySelector('.track-item.active')
      if (active) {
        // Smooth scroll to put active item near the top
        active.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    },
    
    setupObservers() {
      if (this.olderObserver) this.olderObserver.disconnect()
      if (this.newerObserver) this.newerObserver.disconnect()
      
      const list = this.$refs.trackList
      if (!list) return
      
      const olderEl = this.$refs.loadMoreOlder
      if (olderEl) {
        this.olderObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !this.observersPaused && !this.metadataLoading && this.metadataHasMore) {
              this.loadOlder()
            }
          }
        }, { root: list, rootMargin: '100px', threshold: 0 })
        this.olderObserver.observe(olderEl)
      }
      
      const newerEl = this.$refs.loadMoreNewer
      if (newerEl) {
        this.newerObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !this.observersPaused && !this.metadataLoading && this.metadataHasMoreNewer) {
              this.loadNewer()
            }
          }
        }, { root: list, rootMargin: '100px', threshold: 0 })
        this.newerObserver.observe(newerEl)
      }
    },
    
    async loadOlder() {
      if (this.metadataLoading || !this.metadataHasMore) return
      await this.$store.dispatch('fetchMetadataPageAction', { direction: 'older' })
    },
    
    async loadNewer() {
      if (this.metadataLoading || !this.metadataHasMoreNewer) return
      
      const list = this.$refs.trackList
      if (!list) return
      
      const prevScrollHeight = list.scrollHeight
      
      await this.$store.dispatch('fetchMetadataPageAction', { direction: 'newer' })
      
      // After adding items to top, adjust scroll to keep view stable
      this.$nextTick(() => {
        const newScrollHeight = list.scrollHeight
        const addedHeight = newScrollHeight - prevScrollHeight
        if (addedHeight > 0) {
          list.scrollTop += addedHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.metadata-section { flex: 1; display: flex; flex-direction: column; min-height: 0; background: var(--white); }
.metadata-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid var(--gray-100); flex-shrink: 0; gap: 16px; }
.header-left { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.header-right { display: flex; align-items: center; gap: 16px; flex: 1; justify-content: flex-end; }
.metadata-header .label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); }
.btn-scroll-active { 
  width: 24px; 
  height: 24px; 
  border: 1px solid var(--gray-200); 
  border-radius: 4px; 
  background: var(--white); 
  color: var(--gray-500); 
  font-size: 12px; 
  cursor: pointer; 
  display: flex; 
  align-items: center; 
  justify-content: center;
  transition: all 0.15s;
}
.btn-scroll-active:hover { background: var(--gray-50); border-color: var(--gray-300); color: var(--gray-700); }
.current-date { font-size: 13px; color: var(--gray-600); font-weight: 500; flex-shrink: 0; }

/* Search box */
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--gray-50);
  transition: all 0.15s;
  max-width: 200px;
  flex: 1;
}
.search-box.active {
  background: var(--white);
  border-color: var(--gray-300);
  max-width: 280px;
}
.search-box:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.search-icon {
  color: var(--gray-400);
  font-size: 12px;
  flex-shrink: 0;
}
.search-box input {
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--gray-700);
  outline: none;
  width: 100%;
  min-width: 0;
}
.search-box input::placeholder {
  color: var(--gray-400);
}
.btn-clear-search {
  border: none;
  background: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}
.btn-clear-search:hover {
  color: var(--gray-600);
}

/* Search results */
.search-results { padding: 0; }
.search-results-header {
  padding: 12px 24px;
  font-size: 12px;
  color: var(--gray-500);
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-100);
}
.search-status {
  padding: 24px;
  text-align: center;
  color: var(--gray-500);
  font-size: 14px;
}
.search-status svg {
  margin-right: 8px;
}
.search-result {
  border-left: 3px solid transparent;
}
.search-result:hover {
  border-left-color: var(--primary);
}
.track-datetime {
  text-align: right;
  flex-shrink: 0;
}
.track-date {
  font-size: 12px;
  color: var(--gray-500);
  margin-bottom: 2px;
}

.metadata-list { flex: 1; overflow-y: auto; padding: 0 24px; }
.track-item { display: flex; justify-content: space-between; align-items: flex-start; padding: 14px 12px; margin: 0 -12px; border-bottom: 1px solid var(--gray-100); gap: 20px; border-radius: 4px; cursor: pointer; }
.track-item:hover { background: var(--gray-50); }
.track-item:last-child { border-bottom: none; }
.track-item.active { background: #262626; }
.track-item.active .track-title { color: var(--white); font-weight: 500; }
.track-item.active .track-type { color: var(--white) !important; }
.track-item.active .track-time { color: rgba(255, 255, 255, 0.8); }
.track-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.track-title { font-size: 14px; color: var(--gray-700); line-height: 1.4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.track-artist { font-size: 14px; color: var(--gray-700); }
.track-item.active .track-artist { color: var(--white); }
.track-type { font-size: 12px; color: var(--gray-500); }
.track-time { font-size: 12px; color: var(--gray-500); flex-shrink: 0; white-space: nowrap; }
.empty-message { padding: 24px; text-align: center; color: var(--gray-500); font-size: 14px; }
.load-more { padding: 16px; text-align: center; color: var(--gray-500); font-size: 13px; }
.load-more-search span { cursor: pointer; }
.load-more-search span:hover { color: var(--primary); text-decoration: underline; }
</style>
